import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
  Stack,
  useTheme,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SecurityIcon from "@mui/icons-material/Security";
import LockResetIcon from "@mui/icons-material/LockReset";
import EventIcon from "@mui/icons-material/Event";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import { tokens } from "../theme";
import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import StatusChip from "../components/common/StatusChip";
import ConfirmDialog from "../components/common/ConfirmDialog";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { getUserById } from "../redux/actions/userActions";
import {
  forgotPassword,
  resetAuthServerStatus,
} from "../redux/actions/authActions";

const ProfilePage: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useAppDispatch();

  // Active Session User Info from Auth
  const { userInfo, loadingForgotPassword, serverStatus } = useAppSelector(
    (state) => state.auth,
  );

  // Detailed User Record from User module
  const {
    item: user,
    loadingFetchOne: loadingUser,
    errorFetchOne: errorLoadingUser,
  } = useAppSelector((state) => state.user);

  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState<boolean>(false);

  useEffect(() => {
    if (userInfo && userInfo.id) {
      dispatch(getUserById(userInfo.id));
    }
  }, [dispatch, userInfo]);

  useEffect(() => {
    if (
      serverStatus?.operation === "forgot-password" &&
      serverStatus?.status === 200
    ) {
      setIsResetConfirmOpen(false);
      dispatch(resetAuthServerStatus());
    }
  }, [serverStatus, dispatch]);

  const handleTriggerReset = (): void => {
    if (user?.email) {
      dispatch(forgotPassword({ email: user.email }));
    }
  };

  const groupedPermissions = useMemo<Record<string, string[]>>(() => {
    if (!user || !user.permissions) return {};

    return user.permissions.reduce<Record<string, string[]>>(
      (acc, permission) => {
        const [group] = permission.split(":");
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push(permission);
        return acc;
      },
      {},
    );
  }, [user]);

  const renderContent = (): React.ReactNode => {
    if (loadingUser) {
      return <AppCircularProgress />;
    }

    if (errorLoadingUser) {
      return (
        <PageError
          error={errorLoadingUser}
          onRetry={() => {
            if (userInfo?.id) dispatch(getUserById(userInfo.id));
          }}
        />
      );
    }

    if (!user) {
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography sx={{ color: "text.secondary" }}>
            Loading profile data...
          </Typography>
        </Box>
      );
    }

    const fullName = `${user.firstName} ${user.middleName ? `${user.middleName} ` : ""}${user.lastName}`;

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "4fr 8fr" },
          gap: 3,
        }}
      >
        {/* LEFT COLUMN: Identity & Account Security */}
        <Stack spacing={3}>
          <Card
            sx={{
              borderRadius: 2,
              border: `1px solid ${colors.grey[700]}`,
              bgcolor: "background.paper",
            }}
          >
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Avatar
                sx={{
                  bgcolor:
                    colors.gradients?.primary || theme.palette.primary.main,
                  width: 100,
                  height: 100,
                  mx: "auto",
                  mb: 2,
                  boxShadow: 3,
                }}
              >
                <PersonIcon sx={{ fontSize: 50, color: "#fff" }} />
              </Avatar>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                {fullName}
              </Typography>

              <Chip
                label={user.userRole.replace(/_/g, " ")}
                color="primary"
                icon={<AdminPanelSettingsIcon />}
                sx={{ mb: 2, fontWeight: "bold" }}
              />

              <Divider sx={{ my: 2 }} />

              <Stack spacing={2} sx={{ textAlign: "left" }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center" }}
                >
                  <EmailIcon color="action" fontSize="small" />
                  <Typography variant="body2">{user.email}</Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center" }}
                >
                  <CheckCircleIcon color="action" fontSize="small" />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">Status:</Typography>
                    <StatusChip status={user.userStatus} />
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center" }}
                >
                  {user.locked ? (
                    <LockIcon color="error" fontSize="small" />
                  ) : (
                    <LockOpenIcon color="success" fontSize="small" />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: user.locked ? "error.main" : "success.main" }}
                  >
                    {user.locked ? "Account Locked" : "Account Unlocked"}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center" }}
                >
                  <EventIcon color="action" fontSize="small" />
                  <Typography variant="body2">
                    Joined: {new Date(user.createdAt).toLocaleDateString()}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 2,
              border: `1px solid ${colors.grey[700]}`,
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "center", mb: 2 }}
              >
                <SecurityIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Account Security
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Password Management
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.disabled", display: "block", mb: 2 }}
              >
                Think your password has been compromised? Request a secure reset
                link to your registered email to update it.
              </Typography>

              <AppButton
                fullWidth
                variant="outlined"
                color="secondary"
                startIcon={<LockResetIcon />}
                onClick={() => setIsResetConfirmOpen(true)}
              >
                Reset Password
              </AppButton>
            </CardContent>
          </Card>
        </Stack>

        {/* RIGHT COLUMN: Permissions Grid */}
        <Box>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2,
              border: `1px solid ${colors.grey[700]}`,
              bgcolor: "background.paper",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: "center", mb: 3 }}
              >
                <Avatar
                  sx={{
                    background:
                      colors.gradients?.success || theme.palette.success.main,
                    width: 50,
                    height: 50,
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 26, color: "#fff" }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Effective Permissions
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Access rights granted to your role (
                    {user.permissions?.length || 0} total)
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />

              {Object.keys(groupedPermissions).length > 0 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    },
                    gap: 3,
                  }}
                >
                  {Object.entries(groupedPermissions)
                    .sort()
                    .map(([groupName, perms]) => (
                      <Box
                        key={groupName}
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          border: `1px solid ${colors.grey[800]}`,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(0,0,0,0.2)"
                              : "rgba(0,0,0,0.02)",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            textTransform: "capitalize",
                            fontWeight: "bold",
                            mb: 1.5,
                          }}
                        >
                          {groupName.replace(/_/g, " ")}
                        </Typography>
                        <Stack spacing={1}>
                          {perms.sort().map((p) => {
                            const action = p.split(":")[1] || p;
                            return (
                              <Chip
                                key={p}
                                label={action}
                                size="small"
                                variant="outlined"
                                sx={{
                                  justifyContent: "flex-start",
                                  borderRadius: 1,
                                  borderColor: colors.grey[600],
                                }}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                    ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography sx={{ color: "text.secondary" }}>
                    No specific permissions assigned to this account.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        <ConfirmDialog
          open={isResetConfirmOpen}
          onClose={() => setIsResetConfirmOpen(false)}
          onConfirm={handleTriggerReset}
          loading={loadingForgotPassword}
          title="Request Password Reset?"
          description={`We will send a secure password reset link to ${user?.email}. You will need to use that link to set a new password.`}
          confirmText="Send Link"
        />
      </Box>
    );
  };

  return (
    <PageContainer
      title="My Profile"
      subtitle="View your personal details, role, and system access rights."
    >
      {renderContent()}
    </PageContainer>
  );
};

export default ProfilePage;
